import i18n from "@/src/i18n";
import type { MaybeValidProps } from "@core/domain/models/maybe-valid";
import type { Unbrand } from "@core/domain/types/unbrand.type";
import type { ModelError, PartialValid } from "@iot-platform/core";
import {
    NonRecuperableModelError,
    RecuperableModelError,
    partialSafeParse,
} from "@iot-platform/core";
import { merge } from "es-toolkit";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

export const RouteStatusEnum = {
    PARADA: "PARADA",
    DESPLAZAMIENTO: "DESPLAZAMIENTO",
} as const;
export type RouteStatusEnum =
    (typeof RouteStatusEnum)[keyof typeof RouteStatusEnum];
const RouteStatusEnumSchema = z.enum(RouteStatusEnum);

const latitudeRegex = /^-?(([1-8]?\d(\.\d+)?)|90(\.0+)?)$/;
const longitudeRegex = /^-?((1[0-7]\d(\.\d+)?)|([1-9]?\d(\.\d+)?)|180(\.0+)?)$/;

export const ValuesSchema = z.record(
    z.string(),
    z.union([z.string().trim(), z.number()]),
);

const LatitudeSchema = z
    .string()
    .trim()
    .refine((value) => latitudeRegex.test(value), {
        error: () => i18n.t("validation:models.route.invalidLatitude"),
    })
    .transform(Number);

const LongitudeSchema = z
    .string()
    .trim()
    .refine((value) => longitudeRegex.test(value), {
        error: () => i18n.t("validation:models.route.invalidLongitude"),
    })
    .transform(Number);

export const TrackSchema = z.object({
    timestamp: z.number(),
    status: RouteStatusEnumSchema.nullable(),
    position: z.string().trim(),
    latitude: LatitudeSchema,
    longitude: LongitudeSchema,
    values: ValuesSchema,
});

export interface RoutePoint {
    lat: number;
    lng: number;
    status: RouteStatusEnum | null;
    timestamp: number;
}

const RouteSchema = z
    .object({
        tracks: z.array(TrackSchema),
        roads: z.array(z.string().trim()),
    })
    .brand<"Route">();

export type RouteInputProps = z.input<typeof RouteSchema>;
export type RouteProps = z.output<typeof RouteSchema>;

export class Route {
    private constructor(private readonly props: Readonly<RouteProps>) {}

    get tracks(): Readonly<RouteProps["tracks"]> {
        return this.props.tracks;
    }

    get roads(): Readonly<RouteProps["roads"]> {
        return this.props.roads;
    }

    static validate(
        props: Readonly<MaybeValidProps<RouteInputProps>>,
    ): Result<RouteProps, z.ZodError<RouteProps>> {
        const result = RouteSchema.safeParse(props);
        return result.success ? ok(result.data) : err(result.error);
    }

    private static partialValidate(
        props: Readonly<MaybeValidProps<RouteInputProps>>,
    ): Result<RouteProps, PartialValid<typeof Route>> {
        const result = partialSafeParse(RouteSchema, props);

        return result.success
            ? ok(result.data)
            : err({
                  validProps: result.validData,
                  errors: result.error,
              });
    }

    static create(
        props: Readonly<RouteInputProps>,
    ): Result<Route, z.ZodError<RouteProps>> {
        return Route.validate(props).map(
            (validatedProps) => new Route(validatedProps),
        );
    }

    static createWithRecovery(
        props: RouteInputProps,
    ): Result<Route, ModelError<typeof Route>> {
        return Route.partialValidate(props)
            .mapErr((error) => {
                if (!error.validProps.tracks?.length)
                    return new NonRecuperableModelError();

                const defaultProps: Unbrand<RouteProps> = {
                    tracks: [],
                    roads: [],
                };

                const recoveryProps: Unbrand<RouteProps> = merge(
                    defaultProps,
                    error.validProps,
                );

                return new RecuperableModelError<typeof Route>(
                    new Route(recoveryProps as RouteProps),
                    error.errors,
                );
            })
            .map((validatedProps) => new Route(validatedProps));
    }

    toCreateProps(): RouteInputProps {
        return {
            tracks: this.tracks.map((track) => ({
                timestamp: track.timestamp,
                status: track.status,
                position: track.position,
                latitude: String(track.latitude),
                longitude: String(track.longitude),
                values: track.values,
            })),
            roads: [...this.roads],
        };
    }
}

export interface RouteFilter {
    from: number;
    to: number;
    stationId: number;
}
